import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでシミュレータのシミュレーションが成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface ISimulateSimulatorRequestModel {
  /**
   * impulseでのシミュレーション有無
   */
  impulse: boolean;
  /**
   * stepでのシミュレーション有無
   */
  step: boolean;
  /**
   * 比較ファイルのファイルID配列
   */
  comparisonfiles?: string[];
  /**
   * シミュレーション対象のシミュレータID配列
   */
  simulators: string[];
}

function AxiosPost(
  url: string,
  params: ISimulateSimulatorRequestModel,
  token?: string
) {
  if (token) {
    return axios.post(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface ISimulateSimulatorParams
  extends ISimulateSimulatorRequestModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
}

/**
 * シミュレータをシミュレーションします
 * @function simulateSimulator
 * @param {ISimulateSimulatorParams} params - シミュレータのシミュレーションパラメータ
 */
export function simulateSimulator(params: ISimulateSimulatorParams): void {
  const { authService, projectid, impulse, step, comparisonfiles, simulators } =
    params;
  const req: ISimulateSimulatorRequestModel = {
    impulse: impulse,
    step: step,
    comparisonfiles: comparisonfiles,
    simulators: simulators,
  };
  const url = WebAPI.SIMULATION_SIMULATOR(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              simulateSimulatorRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * シミュレータをシミュレーションします
 * @function simulateSimulatorRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ISimulateSimulatorRequestModel} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function simulateSimulatorRetry401(
  authService: AuthService,
  url: string,
  req: ISimulateSimulatorRequestModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}